import type {
  Auction,
  AuctionBid,
  CreditFacility,
  FacilityRequirement,
  IOU,
  IOUEvent,
  KycCase,
  LenderPosition,
  LendingPool,
  Liquidation,
  MarginAccount,
  MarginAccountCollateral,
  OnchainClaim,
  OnchainClaimReviewCase,
  OnchainId,
  User,
  UserFacilityAccess,
  YieldEntry,
  CfModuleWhitelistEntry,
  CfFacilityModule,
} from './types';

function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export const TOKEN_NAMES: Record<string, string> = {
  ABN: 'Ascend Bond Network',
  FMM: 'Fasanara Money Market Fund',
  SNG: 'Singapore Real Estate Fund',
  APX: 'Apex Capital Fund',
  MSM: 'Meridian Stable Fund',
  SBC: 'Silverback Capital',
};

export const CREDIT_FACILITIES: CreditFacility[] = [
  {
    id: 'facility-1',
    name: 'USDC Core Facility',
    status: 'active',
    region: 'Global',
    category: 'Core',
    settlementTokens: ['USDC', 'USDT'],
    collateralTokens: ['ABN', 'SNG', 'FMM'],
    interestRatePct: 5.5,
    maxLtvPct: 67,
    liquidationThresholdPct: 150,
    utilizationPct: 60,
    tvlUsd: 12_400_000,
    borrowedUsd: 7_400_000,
    hasDdf: true,
  },
  {
    id: 'facility-2',
    name: 'ABN Yield Facility',
    status: 'active',
    region: 'Global',
    category: 'Yield',
    settlementTokens: ['USDC'],
    collateralTokens: ['ABN', 'APX'],
    interestRatePct: 4.2,
    maxLtvPct: 77,
    liquidationThresholdPct: 130,
    utilizationPct: 40,
    tvlUsd: 8_600_000,
    borrowedUsd: 3_400_000,
    hasDdf: false,
  },
  {
    id: 'facility-3',
    name: 'SNG Stable Facility',
    status: 'active',
    region: 'Europe',
    category: 'Stable',
    settlementTokens: ['USDC', 'USDT', 'EURC'],
    collateralTokens: ['SNG', 'MSM'],
    interestRatePct: 3.8,
    maxLtvPct: 91,
    liquidationThresholdPct: 110,
    utilizationPct: 87.5,
    tvlUsd: 22_100_000,
    borrowedUsd: 19_300_000,
    hasDdf: true,
  },
  {
    id: 'facility-4',
    name: 'Institutional ABN Prime',
    status: 'active',
    region: 'Global',
    category: 'Institutional',
    settlementTokens: ['USD1', 'USDC'],
    collateralTokens: ['APX', 'SBC'],
    interestRatePct: 12.5,
    maxLtvPct: 50,
    liquidationThresholdPct: 200,
    utilizationPct: 16.6,
    tvlUsd: 1_200_000,
    borrowedUsd: 200_000,
    hasDdf: false,
  },
  {
    id: 'facility-5',
    name: 'FMM Reserve Facility',
    status: 'active',
    region: 'Global',
    category: 'Reserve',
    settlementTokens: ['USDC', 'USDT'],
    collateralTokens: ['FMM', 'ABN'],
    interestRatePct: 5.0,
    maxLtvPct: 72,
    liquidationThresholdPct: 135,
    utilizationPct: 58,
    tvlUsd: 950_000,
    borrowedUsd: 551_000,
    hasDdf: true,
  },
  {
    id: 'facility-6',
    name: 'MSM Income Facility',
    status: 'active',
    region: 'Europe',
    category: 'Income',
    settlementTokens: ['USDT', 'EURC'],
    collateralTokens: ['MSM', 'SNG'],
    interestRatePct: 6.5,
    maxLtvPct: 68,
    liquidationThresholdPct: 150,
    utilizationPct: 42,
    tvlUsd: 1_100_000,
    borrowedUsd: 462_000,
    hasDdf: false,
  },
  {
    id: 'facility-7',
    name: 'SBC Capital Facility',
    status: 'active',
    region: 'Global',
    category: 'Capital',
    settlementTokens: ['USD1'],
    collateralTokens: ['SBC', 'FMM'],
    interestRatePct: 8.3,
    maxLtvPct: 60,
    liquidationThresholdPct: 165,
    utilizationPct: 35,
    tvlUsd: 750_000,
    borrowedUsd: 262_500,
    hasDdf: true,
  },
  {
    id: 'facility-8',
    name: 'APX Opportunity Facility',
    status: 'active',
    region: 'Global',
    category: 'Opportunity',
    settlementTokens: ['USDC', 'USD1'],
    collateralTokens: ['APX', 'FMM', 'MSM'],
    interestRatePct: 9.8,
    maxLtvPct: 55,
    liquidationThresholdPct: 180,
    utilizationPct: 30,
    tvlUsd: 650_000,
    borrowedUsd: 195_000,
    hasDdf: false,
  },
  {
    id: 'facility-9',
    name: 'Meridian Credit Facility',
    status: 'active',
    region: 'Global',
    category: 'Credit',
    settlementTokens: ['USDC', 'USDT', 'USD1'],
    collateralTokens: ['ABN', 'SNG', 'SBC'],
    interestRatePct: 3.2,
    maxLtvPct: 90,
    liquidationThresholdPct: 112,
    utilizationPct: 80,
    tvlUsd: 5_000_000,
    borrowedUsd: 4_000_000,
    hasDdf: true,
  },
  {
    id: 'facility-10',
    name: 'Horizon Yield Facility',
    status: 'paused',
    region: 'Europe',
    category: 'Yield',
    settlementTokens: ['USDT'],
    collateralTokens: ['FMM', 'SBC', 'APX'],
    interestRatePct: 7.8,
    maxLtvPct: 65,
    liquidationThresholdPct: 155,
    utilizationPct: 50,
    tvlUsd: 2_400_000,
    borrowedUsd: 1_200_000,
    hasDdf: false,
  },
  {
    id: 'facility-11',
    name: 'Prime Institutional Facility',
    status: 'active',
    region: 'Global',
    category: 'Institutional',
    settlementTokens: ['USDC'],
    collateralTokens: ['ABN', 'SNG'],
    interestRatePct: 4.0,
    maxLtvPct: 85,
    liquidationThresholdPct: 120,
    utilizationPct: 72,
    tvlUsd: 1_900_000,
    borrowedUsd: 1_310_000,
    hasDdf: true,
  },
  {
    id: 'facility-12',
    name: 'Zenith Multi Facility',
    status: 'shutdown',
    region: 'Global',
    category: 'Multi',
    settlementTokens: ['USDC', 'USDT'],
    collateralTokens: ['FMM', 'MSM', 'SBC'],
    interestRatePct: 5.8,
    maxLtvPct: 71,
    liquidationThresholdPct: 141,
    utilizationPct: 0,
    tvlUsd: 0,
    borrowedUsd: 0,
    hasDdf: false,
  },
];

export const USERS: User[] = [
  {
    id: 'usr-001',
    name: 'Bob Smith',
    wallet: '0x90F8bf6A479f320ead074411a4B0e7944Ea8c9C1',
    email: 'bob.smith@email.com',
    investorType: 'Individual',
    jurisdiction: 'US',
    kycStatus: 'verified',
  },
  {
    id: 'usr-002',
    name: 'Sarah Connor',
    wallet: '0x3a2b1c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    email: 's.connor@skynet.com',
    investorType: 'Individual',
    jurisdiction: 'US',
    kycStatus: 'pending',
  },
  {
    id: 'usr-003',
    name: 'MegaCorp Inc',
    wallet: '0x1234567890abcdef1234567890abcdef12345678',
    email: 'legal@megacorp.com',
    investorType: 'Corporate',
    jurisdiction: 'US',
    kycStatus: 'verified',
  },
  {
    id: 'usr-004',
    name: 'John Doe',
    wallet: '0xabcdef1234567890abcdef1234567890abcdef12',
    email: 'john.doe@gmail.com',
    investorType: 'Individual',
    jurisdiction: 'US',
    kycStatus: 'rejected',
  },
  {
    id: 'usr-005',
    name: 'Aline Winters',
    wallet: '0x1111222233334444555566667777888899990000',
    email: 'aline@north.io',
    investorType: 'Individual',
    jurisdiction: 'CH',
    kycStatus: 'verified',
  },
  {
    id: 'usr-006',
    name: 'North Harbor LLC',
    wallet: '0x2222333344445555666677778888999900001111',
    email: 'ops@northharbor.com',
    investorType: 'Corporate',
    jurisdiction: 'US',
    kycStatus: 'verified',
  },
  {
    id: 'usr-007',
    name: 'Dafna Levi',
    wallet: '0x3333444455556666777788889999000011112222',
    email: 'dafna@levi.ch',
    investorType: 'Individual',
    jurisdiction: 'CH',
    kycStatus: 'pending',
  },
  {
    id: 'usr-008',
    name: 'Kappa Partners',
    wallet: '0x4444555566667777888899990000111122223333',
    email: 'legal@kappapartners.io',
    investorType: 'Corporate',
    jurisdiction: 'SG',
    kycStatus: 'verified',
  },
  {
    id: 'usr-009',
    name: 'Eric Cao',
    wallet: '0x5555666677778888999900001111222233334444',
    email: 'eric.cao@atlas.dev',
    investorType: 'Individual',
    jurisdiction: 'SG',
    kycStatus: 'rejected',
  },
  {
    id: 'usr-010',
    name: 'Vista Capital',
    wallet: '0x6666777788889999000011112222333344445555',
    email: 'compliance@vistacapital.com',
    investorType: 'Corporate',
    jurisdiction: 'UK',
    kycStatus: 'pending',
  },
  {
    id: 'usr-011',
    name: 'Default Searcher',
    wallet: '0x1b75ebf7e0cc2f1f7e9d46da8ed8a6f10000f94e',
    email: 'searcher@ascend.dev',
    investorType: 'Individual',
    jurisdiction: 'US',
    kycStatus: 'verified',
  },
  // Demo-friendly healthy borrowers per facility (Dennis April-28).
  { id: 'usr-101', name: 'Healthy Demo 1', wallet: '0x1aaa01aaaa0000000000000000000000000d0001', email: 'demo1@ascend.dev', investorType: 'Individual', jurisdiction: 'US', kycStatus: 'verified' },
  { id: 'usr-102', name: 'Healthy Demo 2', wallet: '0x2bbb02bbbb0000000000000000000000000d0002', email: 'demo2@ascend.dev', investorType: 'Corporate', jurisdiction: 'US', kycStatus: 'verified' },
  { id: 'usr-103', name: 'Healthy Demo 3', wallet: '0x3ccc03cccc0000000000000000000000000d0003', email: 'demo3@ascend.dev', investorType: 'Individual', jurisdiction: 'EU', kycStatus: 'verified' },
  { id: 'usr-104', name: 'Healthy Demo 4', wallet: '0x4ddd04dddd0000000000000000000000000d0004', email: 'demo4@ascend.dev', investorType: 'Individual', jurisdiction: 'US', kycStatus: 'verified' },
  { id: 'usr-105', name: 'Healthy Demo 5', wallet: '0x5eee05eeee0000000000000000000000000d0005', email: 'demo5@ascend.dev', investorType: 'Corporate', jurisdiction: 'SG', kycStatus: 'verified' },
  { id: 'usr-110', name: 'Healthy Demo 10', wallet: '0xaaaa10aaaa0000000000000000000000000d0010', email: 'demo10@ascend.dev', investorType: 'Individual', jurisdiction: 'EU', kycStatus: 'verified' },
];

export const MARGIN_ACCOUNTS: MarginAccount[] = [
  {
    id: 'ma-1005',
    facilityId: 'facility-1',
    userId: 'usr-001',
    wallet: '0x57f2f9...91ee',
    status: 'warn',
    settlementToken: 'USDC',
    collateralValueUsd: 634_002,
    borrowedUsd: 238_878,
    interestAccrued: 1_904,
    healthFactor: 1.39,
    lastPayment: '14/03/2026',
    nextPayment: '29/03/2026',
  },
  {
    id: 'ma-1010',
    facilityId: 'facility-1',
    userId: 'usr-002',
    wallet: '0x69f8f5...1bd1',
    status: 'warn',
    settlementToken: 'USDC',
    collateralValueUsd: 117_501,
    borrowedUsd: 46_089,
    interestAccrued: 531,
    healthFactor: 1.32,
    lastPayment: '11/02/2026',
    nextPayment: '25/02/2026',
  },
  {
    id: 'ma-1015',
    facilityId: 'facility-1',
    userId: 'usr-003',
    wallet: '0x5321a4...ea39',
    status: 'frozen',
    settlementToken: 'USDC',
    collateralValueUsd: 734_249,
    borrowedUsd: 553_596,
    interestAccrued: 3_210,
    healthFactor: 0,
    lastPayment: '13/02/2026',
    nextPayment: '08/03/2026',
  },
  {
    id: 'ma-1018',
    facilityId: 'facility-1',
    userId: 'usr-004',
    wallet: '0x9e4ab5...c3d6',
    status: 'paused',
    settlementToken: 'USDC',
    collateralValueUsd: 862_843,
    borrowedUsd: 636_397,
    interestAccrued: 3_990,
    healthFactor: 0,
    lastPayment: '18/03/2026',
    nextPayment: '25/04/2026',
  },
  {
    id: 'ma-1020',
    facilityId: 'facility-1',
    userId: 'usr-005',
    wallet: '0xbc6e51...0857',
    status: 'default',
    settlementToken: 'USDC',
    collateralValueUsd: 681_758,
    borrowedUsd: 647_670,
    interestAccrued: 4_480,
    healthFactor: 0.85,
    lastPayment: '22/02/2026',
    nextPayment: '13/03/2026',
  },
  {
    id: 'ma-1025',
    facilityId: 'facility-2',
    userId: 'usr-006',
    wallet: '0xa090ec...154f',
    status: 'warn',
    settlementToken: 'USDC',
    collateralValueUsd: 242_287,
    borrowedUsd: 171_278,
    interestAccrued: 1_052,
    healthFactor: 1.13,
    lastPayment: '19/03/2026',
    nextPayment: '04/04/2026',
  },
  {
    id: 'ma-1030',
    facilityId: 'facility-2',
    userId: 'usr-007',
    wallet: '0x5eede7...0522',
    status: 'warn',
    settlementToken: 'USDC',
    collateralValueUsd: 186_541,
    borrowedUsd: 58_636,
    interestAccrued: 310,
    healthFactor: 1.31,
    lastPayment: '18/03/2026',
    nextPayment: '16/04/2026',
  },
  {
    id: 'ma-1035',
    facilityId: 'facility-3',
    userId: 'usr-008',
    wallet: '0xfc13f5...ea5a',
    status: 'frozen',
    settlementToken: 'USD1',
    collateralValueUsd: 896_699,
    borrowedUsd: 475_467,
    interestAccrued: 2_811,
    healthFactor: 0,
    lastPayment: '06/03/2026',
    nextPayment: '23/03/2026',
  },
  {
    id: 'ma-1038',
    facilityId: 'facility-3',
    userId: 'usr-009',
    wallet: '0x23acf7...b673',
    status: 'paused',
    settlementToken: 'USD1',
    collateralValueUsd: 638_513,
    borrowedUsd: 495_459,
    interestAccrued: 2_255,
    healthFactor: 0,
    lastPayment: '05/03/2026',
    nextPayment: '23/03/2026',
  },
  {
    id: 'ma-1040',
    facilityId: 'facility-3',
    userId: 'usr-010',
    wallet: '0x63db0d...7a6d',
    status: 'default',
    settlementToken: 'USD1',
    collateralValueUsd: 840_194,
    borrowedUsd: 798_185,
    interestAccrued: 5_120,
    healthFactor: 0.51,
    lastPayment: '13/03/2026',
    nextPayment: '11/04/2026',
  },
  {
    id: 'ma-1100',
    facilityId: 'facility-4',
    userId: 'usr-011',
    wallet: '0x71c1a3...3cd1',
    status: 'liquidated',
    settlementToken: 'USDC',
    collateralValueUsd: 94_500,
    borrowedUsd: 88_000,
    interestAccrued: 0,
    healthFactor: 0,
    lastPayment: '10/03/2026',
    nextPayment: '17/03/2026',
  },
  {
    id: 'ma-1160',
    facilityId: 'facility-4',
    userId: 'usr-003',
    wallet: '0xae32f1...94ab',
    status: 'default',
    settlementToken: 'USDC',
    collateralValueUsd: 120_700,
    borrowedUsd: 103_000,
    interestAccrued: 550,
    healthFactor: 0.95,
    lastPayment: '02/03/2026',
    nextPayment: '20/03/2026',
  },
  {
    id: 'ma-2103',
    facilityId: 'facility-5',
    userId: 'usr-006',
    wallet: '0xaaa0f1...9031',
    status: 'default',
    settlementToken: 'USDT',
    collateralValueUsd: 512_300,
    borrowedUsd: 478_400,
    interestAccrued: 2_310,
    healthFactor: 0.94,
    lastPayment: '03/03/2026',
    nextPayment: '19/03/2026',
  },
  {
    id: 'ma-2114',
    facilityId: 'facility-5',
    userId: 'usr-007',
    wallet: '0xbbb0f2...1190',
    status: 'warn',
    settlementToken: 'USDT',
    collateralValueUsd: 230_450,
    borrowedUsd: 194_200,
    interestAccrued: 882,
    healthFactor: 1.08,
    lastPayment: '09/03/2026',
    nextPayment: '25/03/2026',
  },
  {
    id: 'ma-3302',
    facilityId: 'facility-6',
    userId: 'usr-008',
    wallet: '0xccc0f3...22a1',
    status: 'warn',
    settlementToken: 'USD1',
    collateralValueUsd: 120_055,
    borrowedUsd: 98_021,
    interestAccrued: 480,
    healthFactor: 1.17,
    lastPayment: '11/03/2026',
    nextPayment: '27/03/2026',
  },
  {
    id: 'ma-3322',
    facilityId: 'facility-6',
    userId: 'usr-009',
    wallet: '0xddd0f4...ab31',
    status: 'active',
    settlementToken: 'USD1',
    collateralValueUsd: 99_500,
    borrowedUsd: 76_000,
    interestAccrued: 410,
    healthFactor: 1.31,
    lastPayment: '12/03/2026',
    nextPayment: '28/03/2026',
  },
  {
    id: 'ma-5073',
    facilityId: 'facility-8',
    userId: 'usr-011',
    wallet: '0xsearch...5073',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 212_300,
    borrowedUsd: 120_600,
    interestAccrued: 630,
    healthFactor: 1.76,
    lastPayment: '18/03/2026',
    nextPayment: '02/04/2026',
  },
  // facility-7: SBC Capital Facility
  {
    id: 'ma-7001',
    facilityId: 'facility-7',
    userId: 'usr-003',
    wallet: '0xcapital...7001',
    status: 'active',
    settlementToken: 'USD1',
    collateralValueUsd: 320_000,
    borrowedUsd: 180_000,
    interestAccrued: 1240,
    healthFactor: 1.78,
    lastPayment: '20/03/2026',
    nextPayment: '05/04/2026',
  },
  {
    id: 'ma-7002',
    facilityId: 'facility-7',
    userId: 'usr-007',
    wallet: '0xcapital...7002',
    status: 'active',
    settlementToken: 'USD1',
    collateralValueUsd: 185_000,
    borrowedUsd: 82_500,
    interestAccrued: 560,
    healthFactor: 2.24,
    lastPayment: '19/03/2026',
    nextPayment: '03/04/2026',
  },
  // facility-9: Meridian Credit Facility
  {
    id: 'ma-9001',
    facilityId: 'facility-9',
    userId: 'usr-001',
    wallet: '0xmeridian...9001',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 1_800_000,
    borrowedUsd: 1_500_000,
    interestAccrued: 4800,
    healthFactor: 1.20,
    lastPayment: '22/03/2026',
    nextPayment: '06/04/2026',
  },
  {
    id: 'ma-9002',
    facilityId: 'facility-9',
    userId: 'usr-005',
    wallet: '0xmeridian...9002',
    status: 'warn',
    settlementToken: 'USDT',
    collateralValueUsd: 950_000,
    borrowedUsd: 820_000,
    interestAccrued: 2100,
    healthFactor: 1.08,
    lastPayment: '15/03/2026',
    nextPayment: '01/04/2026',
  },
  {
    id: 'ma-9003',
    facilityId: 'facility-9',
    userId: 'usr-008',
    wallet: '0xmeridian...9003',
    status: 'active',
    settlementToken: 'USD1',
    collateralValueUsd: 1_250_000,
    borrowedUsd: 980_000,
    interestAccrued: 3150,
    healthFactor: 1.28,
    lastPayment: '21/03/2026',
    nextPayment: '05/04/2026',
  },
  // facility-10: Horizon Yield Facility (paused)
  {
    id: 'ma-10001',
    facilityId: 'facility-10',
    userId: 'usr-004',
    wallet: '0xhorizon...10001',
    status: 'paused',
    settlementToken: 'USDT',
    collateralValueUsd: 1_100_000,
    borrowedUsd: 550_000,
    interestAccrued: 3500,
    healthFactor: 2.00,
    lastPayment: '10/03/2026',
    nextPayment: '25/03/2026',
  },
  {
    id: 'ma-10002',
    facilityId: 'facility-10',
    userId: 'usr-009',
    wallet: '0xhorizon...10002',
    status: 'paused',
    settlementToken: 'USDT',
    collateralValueUsd: 680_000,
    borrowedUsd: 350_000,
    interestAccrued: 2200,
    healthFactor: 1.94,
    lastPayment: '12/03/2026',
    nextPayment: '27/03/2026',
  },
  // facility-11: Prime Institutional Facility
  {
    id: 'ma-11001',
    facilityId: 'facility-11',
    userId: 'usr-002',
    wallet: '0xprime...11001',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 850_000,
    borrowedUsd: 600_000,
    interestAccrued: 2400,
    healthFactor: 1.42,
    lastPayment: '23/03/2026',
    nextPayment: '07/04/2026',
  },
  {
    id: 'ma-11002',
    facilityId: 'facility-11',
    userId: 'usr-006',
    wallet: '0xprime...11002',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 520_000,
    borrowedUsd: 380_000,
    interestAccrued: 1500,
    healthFactor: 1.37,
    lastPayment: '20/03/2026',
    nextPayment: '04/04/2026',
  },
  {
    id: 'ma-11003',
    facilityId: 'facility-11',
    userId: 'usr-010',
    wallet: '0xprime...11003',
    status: 'warn',
    settlementToken: 'USDC',
    collateralValueUsd: 310_000,
    borrowedUsd: 270_000,
    interestAccrued: 1080,
    healthFactor: 1.12,
    lastPayment: '18/03/2026',
    nextPayment: '02/04/2026',
  },
  // ─── Demo-friendly healthy accounts ──────────────────────────────────────────
  // Per Dennis (April 28): every facility should have at least one active,
  // borrowable account so the borrow flow can be demonstrated. Facilities
  // 6/7/8/9/11 already have active accounts above; the entries below cover
  // facilities 1/2/3/4/5/10. Health factor ≥ 2.0, available credit > 50%.
  {
    id: 'ma-2001',
    facilityId: 'facility-1',
    userId: 'usr-101',
    wallet: '0x1aaa01...d001',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 800_000,
    borrowedUsd: 200_000,
    interestAccrued: 420,
    healthFactor: 2.40,
    lastPayment: '20/03/2026',
    nextPayment: '04/04/2026',
  },
  {
    id: 'ma-2002',
    facilityId: 'facility-2',
    userId: 'usr-102',
    wallet: '0x2bbb02...d002',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 350_000,
    borrowedUsd: 80_000,
    interestAccrued: 165,
    healthFactor: 2.62,
    lastPayment: '18/03/2026',
    nextPayment: '02/04/2026',
  },
  {
    id: 'ma-2003',
    facilityId: 'facility-3',
    userId: 'usr-103',
    wallet: '0x3ccc03...d003',
    status: 'active',
    settlementToken: 'USDT',
    collateralValueUsd: 1_200_000,
    borrowedUsd: 300_000,
    interestAccrued: 580,
    healthFactor: 2.55,
    lastPayment: '21/03/2026',
    nextPayment: '05/04/2026',
  },
  {
    id: 'ma-2004',
    facilityId: 'facility-4',
    userId: 'usr-104',
    wallet: '0x4ddd04...d004',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 500_000,
    borrowedUsd: 120_000,
    interestAccrued: 240,
    healthFactor: 2.30,
    lastPayment: '17/03/2026',
    nextPayment: '01/04/2026',
  },
  {
    id: 'ma-2005',
    facilityId: 'facility-5',
    userId: 'usr-105',
    wallet: '0x5eee05...d005',
    status: 'active',
    settlementToken: 'USDC',
    collateralValueUsd: 950_000,
    borrowedUsd: 220_000,
    interestAccrued: 385,
    healthFactor: 2.48,
    lastPayment: '19/03/2026',
    nextPayment: '03/04/2026',
  },
  {
    id: 'ma-2010',
    facilityId: 'facility-10',
    userId: 'usr-110',
    wallet: '0xaaaa10...d010',
    status: 'active',
    settlementToken: 'EURC',
    collateralValueUsd: 600_000,
    borrowedUsd: 140_000,
    interestAccrued: 245,
    healthFactor: 2.35,
    lastPayment: '22/03/2026',
    nextPayment: '06/04/2026',
  },
];

export const MARGIN_ACCOUNT_COLLATERAL: MarginAccountCollateral[] = [
  { id: 'col-001', marginAccountId: 'ma-1005', tokenSymbol: 'ABN', amount: 500 },
  { id: 'col-002', marginAccountId: 'ma-1005', tokenSymbol: 'SNG', amount: 2000 },
  { id: 'col-003', marginAccountId: 'ma-1010', tokenSymbol: 'ABN', amount: 0.5 },
  { id: 'col-004', marginAccountId: 'ma-1015', tokenSymbol: 'ABN', amount: 1200 },
  { id: 'col-005', marginAccountId: 'ma-1018', tokenSymbol: 'SNG', amount: 4200 },
  { id: 'col-006', marginAccountId: 'ma-1020', tokenSymbol: 'FMM', amount: 3100 },
  { id: 'col-007', marginAccountId: 'ma-1025', tokenSymbol: 'ABN', amount: 3800 },
  { id: 'col-008', marginAccountId: 'ma-1030', tokenSymbol: 'APX', amount: 2600 },
  { id: 'col-009', marginAccountId: 'ma-1035', tokenSymbol: 'SNG', amount: 5000 },
  { id: 'col-010', marginAccountId: 'ma-1038', tokenSymbol: 'MSM', amount: 7000 },
  { id: 'col-011', marginAccountId: 'ma-1040', tokenSymbol: 'MSM', amount: 8200 },
  { id: 'col-012', marginAccountId: 'ma-1100', tokenSymbol: 'APX', amount: 4.1 },
  { id: 'col-013', marginAccountId: 'ma-1160', tokenSymbol: 'SBC', amount: 6.9 },
  { id: 'col-014', marginAccountId: 'ma-2103', tokenSymbol: 'FMM', amount: 12000 },
  { id: 'col-015', marginAccountId: 'ma-2114', tokenSymbol: 'ABN', amount: 1.8 },
  { id: 'col-016', marginAccountId: 'ma-3302', tokenSymbol: 'SNG', amount: 4200 },
  { id: 'col-017', marginAccountId: 'ma-3322', tokenSymbol: 'MSM', amount: 5100 },
  { id: 'col-018', marginAccountId: 'ma-5073', tokenSymbol: 'APX', amount: 5.8 },
  // facility-7
  { id: 'col-019', marginAccountId: 'ma-7001', tokenSymbol: 'SBC', amount: 1500 },
  { id: 'col-020', marginAccountId: 'ma-7001', tokenSymbol: 'FMM', amount: 800 },
  { id: 'col-021', marginAccountId: 'ma-7002', tokenSymbol: 'SBC', amount: 900 },
  // facility-9
  { id: 'col-022', marginAccountId: 'ma-9001', tokenSymbol: 'ABN', amount: 4200 },
  { id: 'col-023', marginAccountId: 'ma-9001', tokenSymbol: 'SNG', amount: 8500 },
  { id: 'col-024', marginAccountId: 'ma-9002', tokenSymbol: 'SBC', amount: 3200 },
  { id: 'col-025', marginAccountId: 'ma-9003', tokenSymbol: 'ABN', amount: 2800 },
  { id: 'col-026', marginAccountId: 'ma-9003', tokenSymbol: 'SBC', amount: 1500 },
  // facility-10
  { id: 'col-027', marginAccountId: 'ma-10001', tokenSymbol: 'FMM', amount: 6000 },
  { id: 'col-028', marginAccountId: 'ma-10001', tokenSymbol: 'SBC', amount: 2200 },
  { id: 'col-029', marginAccountId: 'ma-10002', tokenSymbol: 'APX', amount: 3100 },
  // facility-11
  { id: 'col-030', marginAccountId: 'ma-11001', tokenSymbol: 'ABN', amount: 3500 },
  { id: 'col-031', marginAccountId: 'ma-11001', tokenSymbol: 'SNG', amount: 4100 },
  { id: 'col-032', marginAccountId: 'ma-11002', tokenSymbol: 'ABN', amount: 2100 },
  { id: 'col-033', marginAccountId: 'ma-11003', tokenSymbol: 'SNG', amount: 1800 },
  // Collateral for the demo-friendly healthy accounts (ma-2001..ma-2010).
  // Each account gets enough collateral to back the borrow shown on its row,
  // with realistic token splits so the borrow/withdraw token pickers work.
  { id: 'col-034', marginAccountId: 'ma-2001', tokenSymbol: 'ABN', amount: 5500 },
  { id: 'col-035', marginAccountId: 'ma-2001', tokenSymbol: 'SNG', amount: 2800 },
  { id: 'col-036', marginAccountId: 'ma-2002', tokenSymbol: 'FMM', amount: 1800 },
  { id: 'col-037', marginAccountId: 'ma-2003', tokenSymbol: 'APX', amount: 4400 },
  { id: 'col-038', marginAccountId: 'ma-2003', tokenSymbol: 'ABN', amount: 6200 },
  { id: 'col-039', marginAccountId: 'ma-2004', tokenSymbol: 'MSM', amount: 3000 },
  { id: 'col-040', marginAccountId: 'ma-2005', tokenSymbol: 'SBC', amount: 2400 },
  { id: 'col-041', marginAccountId: 'ma-2005', tokenSymbol: 'FMM', amount: 1500 },
  { id: 'col-042', marginAccountId: 'ma-2010', tokenSymbol: 'ABN', amount: 3200 },
];

export const KYC_CASES: KycCase[] = [
  { id: 'rev-001', userId: 'usr-001', facilityId: 'facility-1', provider: 'Onfido', submittedDate: '18/01/2026', riskScore: 15, reviewStatus: 'under_review' },
  { id: 'rev-002', userId: 'usr-002', facilityId: 'facility-1', provider: 'Sumsub', submittedDate: '19/01/2026', riskScore: 85, reviewStatus: 'under_review' },
  { id: 'rev-003', userId: 'usr-003', facilityId: 'facility-1', provider: 'Onfido', submittedDate: '15/01/2026', riskScore: 5, reviewStatus: 'approved' },
  { id: 'rev-004', userId: 'usr-004', facilityId: 'facility-2', provider: 'Sumsub', submittedDate: '10/01/2026', riskScore: 95, reviewStatus: 'rejected' },
  { id: 'rev-005', userId: 'usr-005', facilityId: 'facility-2', provider: 'Onfido', submittedDate: '21/01/2026', riskScore: 29, reviewStatus: 'under_review' },
  { id: 'rev-006', userId: 'usr-006', facilityId: 'facility-2', provider: 'Onfido', submittedDate: '09/01/2026', riskScore: 18, reviewStatus: 'approved' },
  { id: 'rev-007', userId: 'usr-007', facilityId: 'facility-3', provider: 'Sumsub', submittedDate: '12/01/2026', riskScore: 46, reviewStatus: 'under_review' },
  { id: 'rev-008', userId: 'usr-008', facilityId: 'facility-3', provider: 'Onfido', submittedDate: '14/01/2026', riskScore: 32, reviewStatus: 'approved' },
  { id: 'rev-009', userId: 'usr-009', facilityId: 'facility-3', provider: 'Sumsub', submittedDate: '08/01/2026', riskScore: 78, reviewStatus: 'rejected' },
  { id: 'rev-010', userId: 'usr-010', facilityId: 'facility-1', provider: 'Onfido', submittedDate: '16/01/2026', riskScore: 24, reviewStatus: 'under_review' },
];

export const LIQUIDATIONS: Liquidation[] = [
  {
    id: 'liq-001',
    facilityId: 'facility-3',
    marginAccountId: 'ma-1040',
    wallet: '0x63db0d...7a6d',
    workflowStatus: 'not_started',
    debtAmountUsd: 798_185,
    collateralAmountUsd: 840_194,
    healthFactor: 0.51,
    timeInDefault: '17h',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-002',
    facilityId: 'facility-1',
    marginAccountId: 'ma-1020',
    wallet: '0xbc6e51...0857',
    workflowStatus: 'not_started',
    debtAmountUsd: 647_670,
    collateralAmountUsd: 681_758,
    healthFactor: 0.85,
    timeInDefault: '1h',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-003',
    facilityId: 'facility-5',
    marginAccountId: 'ma-2103',
    wallet: '0xaaa0f1...9031',
    workflowStatus: 'in_progress',
    debtAmountUsd: 478_400,
    collateralAmountUsd: 512_300,
    healthFactor: 0.94,
    timeInDefault: '53m',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-004',
    facilityId: 'facility-5',
    marginAccountId: 'ma-2114',
    wallet: '0xbbb0f2...1190',
    workflowStatus: 'not_started',
    debtAmountUsd: 194_200,
    collateralAmountUsd: 230_450,
    healthFactor: 1.08,
    timeInDefault: '11m',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-005',
    facilityId: 'facility-6',
    marginAccountId: 'ma-3302',
    wallet: '0xccc0f3...22a1',
    workflowStatus: 'not_started',
    debtAmountUsd: 98_021,
    collateralAmountUsd: 120_055,
    healthFactor: 1.17,
    timeInDefault: '4m',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-006',
    facilityId: 'facility-6',
    marginAccountId: 'ma-3322',
    wallet: '0xddd0f4...ab31',
    workflowStatus: 'not_started',
    debtAmountUsd: 76_000,
    collateralAmountUsd: 99_500,
    healthFactor: 1.31,
    timeInDefault: '2m',
    executedAt: null,
    recoveryRatePct: 0,
  },
  {
    id: 'liq-007',
    facilityId: 'facility-8',
    marginAccountId: 'ma-5073',
    wallet: '0xsearch...5073',
    workflowStatus: 'liquidated',
    debtAmountUsd: 120_600,
    collateralAmountUsd: 212_300,
    healthFactor: 0,
    timeInDefault: '2d 3h',
    executedAt: '2026-03-20',
    recoveryRatePct: 102.4,
  },
  {
    id: 'liq-008',
    facilityId: 'facility-4',
    marginAccountId: 'ma-1100',
    wallet: '0x71c1a3...3cd1',
    workflowStatus: 'liquidated',
    debtAmountUsd: 88_000,
    collateralAmountUsd: 94_500,
    healthFactor: 0,
    timeInDefault: '1d 12h',
    executedAt: '2026-03-18',
    recoveryRatePct: 96.1,
  },
  {
    id: 'liq-009',
    facilityId: 'facility-4',
    marginAccountId: 'ma-1160',
    wallet: '0xae32f1...94ab',
    workflowStatus: 'liquidated',
    debtAmountUsd: 103_000,
    collateralAmountUsd: 120_700,
    healthFactor: 0,
    timeInDefault: '18h',
    executedAt: '2026-03-15',
    recoveryRatePct: 91.5,
  },
];

// Index collateral by margin account for auction generation
const collateralByMarginAccount = new Map<string, typeof MARGIN_ACCOUNT_COLLATERAL>();
for (const col of MARGIN_ACCOUNT_COLLATERAL) {
  if (!collateralByMarginAccount.has(col.marginAccountId)) {
    collateralByMarginAccount.set(col.marginAccountId, []);
  }
  collateralByMarginAccount.get(col.marginAccountId)!.push(col);
}

// Build a list of facilities that have margin accounts (for auction generation)
const FACILITIES_WITH_ACCOUNTS = CREDIT_FACILITIES.filter((f) =>
  MARGIN_ACCOUNTS.some((a) => a.facilityId === f.id),
);

// Generate one auction per margin account (no duplicates).
// Shuffle margin accounts deterministically, then take up to 20.
const shuffledAccounts = [...MARGIN_ACCOUNTS].sort(
  (a, b) => seededRandom(a.id.length * 7 + 100) - seededRandom(b.id.length * 7 + 200),
);
const AUCTION_COUNT = Math.min(20, shuffledAccounts.length);

export const AUCTIONS: Auction[] = shuffledAccounts.slice(0, AUCTION_COUNT).map((marginAccount, idx) => {
  const seed = idx + 100;
  const facility = CREDIT_FACILITIES.find((f) => f.id === marginAccount.facilityId)!;
  const accountCollateral = collateralByMarginAccount.get(marginAccount.id) ?? [];

  // Pick the first collateral token from the account (or facility fallback)
  const primaryCollateral = accountCollateral[0];
  const tokenSymbol = primaryCollateral?.tokenSymbol ?? facility.collateralTokens[0] ?? 'ABN';
  const collateralAmount = primaryCollateral?.amount ?? +(1 + seededRandom(seed * 11) * 14).toFixed(4);

  // Derive USD values from the margin account
  const collateralValueUsd = marginAccount.collateralValueUsd;
  const debtAmountUsd = marginAccount.borrowedUsd + marginAccount.interestAccrued;
  const reservePriceUsd = Math.min(
    Math.round(debtAmountUsd * (1.02 + seededRandom(seed * 19) * 0.08)),
    collateralValueUsd - 1,
  );
  const bidCount = Math.floor(seededRandom(seed * 23) * 12);
  const statusRoll = seededRandom(seed * 31);
  const status = statusRoll > 0.82 ? 'settled' : statusRoll > 0.65 ? 'ending_soon' : 'active';
  const hours = Math.floor(seededRandom(seed * 37) * 11);
  const minutes = Math.floor(seededRandom(seed * 41) * 59);

  return {
    id: `DDF-${2041 + idx}`,
    facilityId: facility.id,
    marginAccountId: marginAccount.id,
    title: `${tokenSymbol} Auction`,
    status,
    tokenSymbol,
    tokenName: TOKEN_NAMES[tokenSymbol] ?? tokenSymbol,
    collateralAmount,
    collateralValueUsd,
    debtAmountUsd,
    reservePriceUsd,
    currentBidUsd: 0, // patched after AUCTION_BIDS are generated
    bidCount,
    timeRemaining: status === 'settled' ? '-' : `${hours}h ${minutes}m`,
    settlementToken: facility.settlementTokens[0] ?? 'USDC',
  };
});

export const AUCTION_BIDS: AuctionBid[] = AUCTIONS.flatMap((auction, index) => {
  if (auction.bidCount === 0) return [];
  const baseSeed = index + 500;
  const count = auction.bidCount;
  // Start bids at 70-90% of reserve so not all auctions immediately exceed reserve price
  const startFraction = 0.7 + seededRandom(baseSeed * 47) * 0.2;
  let running = Math.round(auction.reservePriceUsd * startFraction);
  return Array.from({ length: count }, (_, bidIndex) => {
    const seed = baseSeed + bidIndex;
    const increment = Math.round(running * (0.006 + seededRandom(seed * 3) * 0.03));
    running += increment;
    return {
      id: `bid-${auction.id}-${bidIndex + 1}`,
      auctionId: auction.id,
      bidderWallet: `0x${Math.floor(seededRandom(seed * 7) * 0xffffff)
        .toString(16)
        .padStart(6, '0')}...${Math.floor(seededRandom(seed * 11) * 0xffff)
        .toString(16)
        .padStart(4, '0')}`,
      amountUsd: running,
      timestamp: `22/03/2026, ${String((20 + bidIndex) % 24).padStart(2, '0')}:${String(Math.floor(seededRandom(seed * 13) * 59)).padStart(2, '0')}:00`,
      txHash: `0x${Math.floor(seededRandom(seed * 17) * 0xffffff)
        .toString(16)
        .padStart(6, '0')}...${Math.floor(seededRandom(seed * 19) * 0xfff)
        .toString(16)
        .padStart(3, '0')}`,
      isLeading: bidIndex === count - 1,
    };
  });
});

// Patch currentBidUsd from actual generated bids so timeline and auction data are consistent
for (const auction of AUCTIONS) {
  const auctionBids = AUCTION_BIDS.filter((b) => b.auctionId === auction.id);
  if (auctionBids.length > 0) {
    const lastBid = auctionBids[auctionBids.length - 1];
    auction.currentBidUsd =
      auction.status === 'settled' ? Math.max(lastBid.amountUsd, auction.reservePriceUsd) : lastBid.amountUsd;
  }
}

export const FACILITY_REQUIREMENTS: FacilityRequirement[] = [
  {
    id: 'req-001',
    facilityId: 'facility-1',
    requirementType: 'external_kyc_claim',
    claimType: 'partner_membership',
    description: 'Must hold a Partner Membership claim',
  },
  {
    id: 'req-002',
    facilityId: 'facility-3',
    requirementType: 'internal_claim',
    claimType: 'corporate_email_verified',
    description: 'Must have Corporate Email Verified claim',
  },
  {
    id: 'req-003',
    facilityId: 'facility-4',
    requirementType: 'external_kyc_claim',
    claimType: 'qualified_premium_investor',
    description: 'Must be a Qualified Premium Investor',
  },
  {
    id: 'req-004',
    facilityId: 'facility-4',
    requirementType: 'internal_claim',
    claimType: 'corporate_email_verified',
    description: 'Must have Corporate Email Verified claim',
  },
  {
    id: 'req-005',
    facilityId: 'facility-10',
    requirementType: 'external_kyc_claim',
    claimType: 'kyc_eu_resident',
    description: 'Must be a verified EU resident',
  },
];

export const ONCHAIN_IDS: OnchainId[] = [
  {
    id: 'oid-1',
    userId: 'usr-011',
    address: '0x1234567890abcdef1234567890abcdef12345678',
    source: 'ascend',
  },
  {
    id: 'oid-2',
    userId: 'usr-011',
    address: '0xabcdef1234567890abcdef1234567890abcdef12',
    source: 'external',
  },
  {
    id: 'oid-3',
    userId: 'usr-003',
    address: '0x5234567890abcdef1234567890abcdef12345678',
    source: 'erc3643',
  },
  {
    id: 'oid-4',
    userId: 'usr-004',
    address: '0x6234567890abcdef1234567890abcdef12345678',
    source: 'external',
  },
  {
    id: 'oid-5',
    userId: 'usr-006',
    address: '0x7234567890abcdef1234567890abcdef12345678',
    source: 'ascend',
  },
  {
    id: 'oid-6',
    userId: 'usr-007',
    address: '0x8234567890abcdef1234567890abcdef12345678',
    source: 'external',
  },
  {
    id: 'oid-7',
    userId: 'usr-008',
    address: '0x9234567890abcdef1234567890abcdef12345678',
    source: 'ascend',
  },
  {
    id: 'oid-8',
    userId: 'usr-009',
    address: '0xa234567890abcdef1234567890abcdef12345678',
    source: 'external',
  },
  {
    id: 'oid-9',
    userId: 'usr-010',
    address: '0xb234567890abcdef1234567890abcdef12345678',
    source: 'erc3643',
  },
];

export const ONCHAIN_CLAIMS: OnchainClaim[] = [
  {
    id: 'claim-001',
    onchainId: 'oid-1',
    claimType: 'partner_membership',
    issuer: 'ascend',
    value: 'verified',
    issuedAt: '2025-01-15T10:00:00Z',
  },
  {
    id: 'claim-002',
    onchainId: 'oid-1',
    claimType: 'corporate_email_verified',
    issuer: 'ascend',
    value: 'verified',
    issuedAt: '2025-02-10T11:00:00Z',
  },
  {
    id: 'claim-003',
    onchainId: 'oid-2',
    claimType: 'qualified_premium_investor',
    issuer: 'sec_registry',
    value: 'verified',
    issuedAt: '2025-02-01T14:00:00Z',
  },
  {
    id: 'claim-004',
    onchainId: 'oid-2',
    claimType: 'kyc_verified',
    issuer: 'external_kyc_provider',
    value: 'verified',
    issuedAt: '2025-01-25T16:00:00Z',
  },
  {
    id: 'claim-005',
    onchainId: 'oid-3',
    claimType: 'institutional_verified',
    issuer: 'ascend',
    value: 'verified',
    issuedAt: '2025-02-05T12:00:00Z',
  },
];

export const ONCHAIN_CLAIM_REVIEW_CASES: OnchainClaimReviewCase[] = [
  {
    id: 'ocr-001',
    facilityId: 'facility-1',
    userId: 'usr-011',
    onchainId: 'oid-1',
    claimType: 'partner_membership',
    provider: 'Ascend',
    issuer: 'ascend',
    jurisdiction: 'US',
    submittedDate: '20/01/2026',
    riskScore: 18,
    reviewStatus: 'under_review',
  },
  {
    id: 'ocr-002',
    facilityId: 'facility-2',
    userId: 'usr-006',
    onchainId: 'oid-5',
    claimType: 'qualified_premium_investor',
    provider: 'Ascend',
    issuer: 'sec_registry',
    jurisdiction: 'US',
    submittedDate: '19/01/2026',
    riskScore: 41,
    reviewStatus: 'under_review',
  },
  {
    id: 'ocr-003',
    facilityId: 'facility-3',
    userId: 'usr-003',
    onchainId: 'oid-3',
    claimType: 'corporate_email_verified',
    provider: 'Ascend',
    issuer: 'ascend',
    jurisdiction: 'US',
    submittedDate: '16/01/2026',
    riskScore: 12,
    reviewStatus: 'approved',
  },
  {
    id: 'ocr-004',
    facilityId: 'facility-4',
    userId: 'usr-004',
    onchainId: 'oid-4',
    claimType: 'corporate_email_verified',
    provider: 'Ascend',
    issuer: 'ascend',
    jurisdiction: 'US',
    submittedDate: '14/01/2026',
    riskScore: 79,
    reviewStatus: 'rejected',
  },
  {
    id: 'ocr-005',
    facilityId: 'facility-5',
    userId: 'usr-007',
    onchainId: 'oid-6',
    claimType: 'qualified_premium_investor',
    provider: 'Ascend',
    issuer: 'external_kyc_provider',
    jurisdiction: 'CH',
    submittedDate: '21/01/2026',
    riskScore: 33,
    reviewStatus: 'under_review',
  },
  {
    id: 'ocr-006',
    facilityId: 'facility-6',
    userId: 'usr-008',
    onchainId: 'oid-7',
    claimType: 'corporate_email_verified',
    provider: 'Ascend',
    issuer: 'ascend',
    jurisdiction: 'SG',
    submittedDate: '13/01/2026',
    riskScore: 9,
    reviewStatus: 'approved',
  },
  {
    id: 'ocr-007',
    facilityId: 'facility-8',
    userId: 'usr-009',
    onchainId: 'oid-8',
    claimType: 'partner_membership',
    provider: 'Ascend',
    issuer: 'ascend',
    jurisdiction: 'SG',
    submittedDate: '11/01/2026',
    riskScore: 88,
    reviewStatus: 'rejected',
  },
  {
    id: 'ocr-008',
    facilityId: 'facility-11',
    userId: 'usr-010',
    onchainId: 'oid-9',
    claimType: 'qualified_premium_investor',
    provider: 'Ascend',
    issuer: 'sec_registry',
    jurisdiction: 'UK',
    submittedDate: '22/01/2026',
    riskScore: 27,
    reviewStatus: 'under_review',
  },
];

export const USER_FACILITY_ACCESS: UserFacilityAccess[] = [
  { userId: 'usr-011', facilityId: 'facility-1' },
  { userId: 'usr-011', facilityId: 'facility-2' },
  { userId: 'usr-011', facilityId: 'facility-3' },
  { userId: 'usr-011', facilityId: 'facility-5' },
  { userId: 'usr-011', facilityId: 'facility-6' },
  { userId: 'usr-011', facilityId: 'facility-8' },
  { userId: 'usr-011', facilityId: 'facility-9' },
  { userId: 'usr-011', facilityId: 'facility-11' },
];

// ─── Lending Pools ────────────────────────────────────────────────────────────
// One pool per active facility, named after the facility's primary settlement token.

export const LENDING_POOLS: LendingPool[] = CREDIT_FACILITIES
  .filter((f) => f.status === 'active')
  .map((f, i) => {
    const token = f.settlementTokens[0] ?? 'USDC';
    return {
      id: `pool-${i + 1}`,
      facilityId: f.id,
      name: `${token} Lending Pool`,
      tvlUsd: Math.round(f.tvlUsd * (0.3 + seededRandom(i * 7 + 200) * 0.4)),
      utilizationPct: +(50 + seededRandom(i * 7 + 201) * 40).toFixed(1),
      apyPct: +(3 + seededRandom(i * 7 + 202) * 9).toFixed(1),
    };
  });

// ─── Lender Positions ────────────────────────────────────────────────────────

const IOU_AFFECTED_POOL_INDICES = [2, 5, 7]; // pools that have reduced withdrawable %

// usr-011 positions (original — client-facing user)
const USR011_POSITIONS: LenderPosition[] = LENDING_POOLS.map((pool, i) => {
  const deposited = Math.round(5000 + seededRandom(i * 5 + 300) * 85000);
  const apy = pool.apyPct / 100;
  const timeInYears = 0.1 + seededRandom(i * 5 + 301) * 0.4;
  const earned = Math.round(deposited * apy * timeInYears);
  const currentValue = deposited + earned;
  const isIouAffected = IOU_AFFECTED_POOL_INDICES.includes(i);
  const withdrawable = isIouAffected
    ? Math.round(50 + seededRandom(i * 5 + 302) * 35)
    : 100;

  return {
    id: `lpos-${i + 1}`,
    poolId: pool.id,
    userId: 'usr-011',
    depositedUsd: deposited,
    lpTokens: currentValue, // 1:1 LP-token to value for simplicity
    earnedYieldUsd: earned,
    withdrawablePct: withdrawable,
  };
});

// Additional lender positions so pools have realistic lender counts (3-6 per pool)
const EXTRA_LENDERS = ['usr-001', 'usr-002', 'usr-003', 'usr-004', 'usr-005'];
const EXTRA_POSITIONS: LenderPosition[] = LENDING_POOLS.flatMap((pool, poolIdx) => {
  const lenderCount = 2 + Math.floor(seededRandom(poolIdx * 13 + 500) * 4); // 2-5 extra per pool
  return EXTRA_LENDERS.slice(0, lenderCount).map((userId, lenderIdx) => {
    const seed = poolIdx * 100 + lenderIdx * 7 + 600;
    const deposited = Math.round(2000 + seededRandom(seed) * 50000);
    const apy = pool.apyPct / 100;
    const timeInYears = 0.05 + seededRandom(seed + 1) * 0.3;
    const earned = Math.round(deposited * apy * timeInYears);
    const currentValue = deposited + earned;
    const isIouAffected = IOU_AFFECTED_POOL_INDICES.includes(poolIdx);
    const withdrawable = isIouAffected
      ? Math.round(60 + seededRandom(seed + 2) * 30)
      : 100;

    return {
      id: `lpos-extra-${poolIdx}-${lenderIdx}`,
      poolId: pool.id,
      userId,
      depositedUsd: deposited,
      lpTokens: currentValue,
      earnedYieldUsd: earned,
      withdrawablePct: withdrawable,
    };
  });
});

export const LENDER_POSITIONS: LenderPosition[] = [...USR011_POSITIONS, ...EXTRA_POSITIONS];

// ─── IOUs (tokens) ───────────────────────────────────────────────────────────

export const IOUS: IOU[] = IOU_AFFECTED_POOL_INDICES.map((poolIdx) => {
  const pool = LENDING_POOLS[poolIdx]!;
  const facility = CREDIT_FACILITIES.find((f) => f.id === pool.facilityId);
  const facilityName = facility?.name ?? pool.facilityId;
  const baseSeed = poolIdx * 100 + 400;
  const original = Math.round(20_000 + seededRandom(baseSeed) * 80_000);
  const repaid = Math.round(original * seededRandom(baseSeed + 1) * 0.6);
  const remaining = original - repaid;
  const status = remaining === 0 ? 'settled' as const : repaid > 0 ? 'partially_repaid' as const : 'outstanding' as const;

  return {
    id: `iou-${pool.id}`,
    poolId: pool.id,
    facilityName,
    originalAmountUsd: original,
    repaidAmountUsd: repaid,
    remainingAmountUsd: remaining,
    status,
  };
});

// ─── IOU Events (mint/burn log) ──────────────────────────────────────────────

export const IOU_EVENTS: IOUEvent[] = IOUS.flatMap((iou) => {
  const events: IOUEvent[] = [
    {
      id: `ioue-${iou.id}-mint`,
      iouId: iou.id,
      poolId: iou.poolId,
      eventType: 'mint',
      amountUsd: iou.originalAmountUsd,
      createdAt: '2026-03-15',
    },
  ];
  if (iou.repaidAmountUsd > 0) {
    events.push({
      id: `ioue-${iou.id}-burn`,
      iouId: iou.id,
      poolId: iou.poolId,
      eventType: 'burn',
      amountUsd: iou.repaidAmountUsd,
      createdAt: '2026-03-20',
    });
  }
  return events;
});

// ─── Yield History (per-pool, for usr-011) ───────────────────────────────────

export const YIELD_HISTORY: YieldEntry[] = LENDING_POOLS.flatMap((pool, poolIdx) => {
  const entries: YieldEntry[] = [];
  let cumulative = 0;
  const weekCount = 12; // 12 weeks per pool
  for (let i = 0; i < weekCount; i++) {
    const weekStart = new Date(2025, 11, 30 + i * 7); // start late Dec 2025
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const yieldAmt = Math.round(20 + seededRandom(poolIdx * 100 + i * 2 + 500) * 150);
    cumulative += yieldAmt;
    const fmtDate = (d: Date) =>
      `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    entries.push({
      id: `yield-${pool.id}-${i + 1}`,
      poolId: pool.id,
      userId: 'usr-011',
      weekLabel: `${fmtDate(weekStart)} to ${fmtDate(weekEnd)}`,
      yieldUsd: yieldAmt,
      cumulativeUsd: cumulative,
    });
  }
  return entries;
});

export const CF_MODULE_WHITELIST: CfModuleWhitelistEntry[] = [
  { id: 'mod-1', moduleType: 'Auction', name: 'Dutch Auction v2', description: 'Descending price auction with automatic settlement', chainAddress: '0xAuc1...Dutch', chainId: '11155111', updatedAt: '2026-01-15T10:00:00Z' },
  { id: 'mod-2', moduleType: 'Auction', name: 'Batch Clearing', description: 'Batch order matching with uniform clearing price', chainAddress: '0xAuc2...Batch', chainId: '11155111', updatedAt: '2026-01-15T10:00:00Z' },
  { id: 'mod-3', moduleType: 'Auction', name: 'Sealed Bid', description: 'Sealed-bid auction with reveal phase', chainAddress: '0xAuc3...Sealed', chainId: '11155111', updatedAt: '2026-01-15T10:00:00Z' },
  { id: 'mod-4', moduleType: 'Fee', name: 'Tiered Fee v2', description: 'Volume-based tiered fee structure with management and performance fees', chainAddress: '0xFee1...Tiered', chainId: '11155111', updatedAt: '2026-01-20T08:00:00Z' },
  { id: 'mod-5', moduleType: 'Fee', name: 'Flat Fee v1', description: 'Simple flat-rate fee applied uniformly across all transactions', chainAddress: '0xFee2...Flat', chainId: '11155111', updatedAt: '2026-01-20T08:00:00Z' },
  { id: 'mod-6', moduleType: 'Fee', name: 'RWA Split Fee', description: 'Split fee model for real-world asset facilities with originator/manager split', chainAddress: '0xFee3...RWA', chainId: '11155111', updatedAt: '2026-02-01T09:00:00Z' },
  { id: 'mod-7', moduleType: 'Risk', name: 'Standard Risk v2', description: 'Default risk engine with LTV-based health factor calculations', chainAddress: '0xRisk1...Std', chainId: '11155111', updatedAt: '2026-01-10T12:00:00Z' },
  { id: 'mod-8', moduleType: 'Risk', name: 'Aggressive Growth', description: 'Higher-risk tolerance model for growth-oriented facilities', chainAddress: '0xRisk2...Aggr', chainId: '11155111', updatedAt: '2026-01-10T12:00:00Z' },
  { id: 'mod-9', moduleType: 'Risk', name: 'Institutional Guardrail', description: 'Conservative risk model with institutional-grade safeguards', chainAddress: '0xRisk3...Inst', chainId: '11155111', updatedAt: '2026-02-05T14:00:00Z' },
  { id: 'mod-10', moduleType: 'Oracle', name: 'Chainlink Core', description: 'Chainlink decentralized oracle network for price feeds', chainAddress: '0xOra1...CLink', chainId: '11155111', updatedAt: '2026-01-08T11:00:00Z' },
  { id: 'mod-11', moduleType: 'Oracle', name: 'Pyth Fast Oracle', description: 'Pyth Network low-latency oracle with cross-chain support', chainAddress: '0xOra2...Pyth', chainId: '11155111', updatedAt: '2026-01-08T11:00:00Z' },
  { id: 'mod-12', moduleType: 'Oracle', name: 'RedStone Hybrid', description: 'RedStone modular oracle with on-demand and push data delivery', chainAddress: '0xOra3...Red', chainId: '11155111', updatedAt: '2026-02-10T16:00:00Z' },
  { id: 'mod-13', moduleType: 'Valuation', name: 'NAV Calculator v1', description: 'Net asset value calculator for fund-based collateral', chainAddress: '0xVal1...NAV', chainId: '11155111', updatedAt: '2026-02-12T10:00:00Z' },
  { id: 'mod-14', moduleType: 'Liquidity', name: 'AMM Pool Router', description: 'Automated market maker routing for liquidity provisioning', chainAddress: '0xLiq1...AMM', chainId: '11155111', updatedAt: '2026-02-15T09:00:00Z' },
  { id: 'mod-15', moduleType: 'Compliance', name: 'Standard Institutional Compliance', description: 'Modular compliance bundling tx-frequency, borrow-size, and US-jurisdiction rules', chainAddress: '0xCmp1...Std', chainId: '11155111', updatedAt: '2026-02-20T10:00:00Z' },
  { id: 'mod-16', moduleType: 'Compliance', name: 'Aggressive RWA Compliance', description: 'Loosened caps and longer trading windows for high-velocity RWA facilities', chainAddress: '0xCmp2...RWA', chainId: '11155111', updatedAt: '2026-02-20T10:00:00Z' },
  { id: 'mod-17', moduleType: 'Compliance', name: 'EU Qualified Compliance', description: 'MiFID II Qualified-Investor framing with EU jurisdiction restrictions', chainAddress: '0xCmp3...EU', chainId: '11155111', updatedAt: '2026-02-22T11:00:00Z' },
  { id: 'mod-18', moduleType: 'Compliance', name: 'Conservative Treasury Compliance', description: 'Tight caps and short windows for treasury-grade facilities', chainAddress: '0xCmp4...Trsy', chainId: '11155111', updatedAt: '2026-02-25T13:00:00Z' },
];

export const CF_FACILITY_MODULES: CfFacilityModule[] = [
  // facility-1: USDC Core Facility
  { id: 'fm-1', facilityId: 'facility-1', moduleId: 'mod-1' },
  { id: 'fm-2', facilityId: 'facility-1', moduleId: 'mod-4' },
  { id: 'fm-3', facilityId: 'facility-1', moduleId: 'mod-7' },
  { id: 'fm-4', facilityId: 'facility-1', moduleId: 'mod-10' },
  { id: 'fm-5', facilityId: 'facility-1', moduleId: 'mod-13' },
  // facility-2: WBTC Yield Facility
  { id: 'fm-6', facilityId: 'facility-2', moduleId: 'mod-2' },
  { id: 'fm-7', facilityId: 'facility-2', moduleId: 'mod-5' },
  { id: 'fm-8', facilityId: 'facility-2', moduleId: 'mod-8' },
  { id: 'fm-9', facilityId: 'facility-2', moduleId: 'mod-11' },
  // facility-3: DAI Stable Facility
  { id: 'fm-10', facilityId: 'facility-3', moduleId: 'mod-1' },
  { id: 'fm-11', facilityId: 'facility-3', moduleId: 'mod-6' },
  { id: 'fm-12', facilityId: 'facility-3', moduleId: 'mod-9' },
  { id: 'fm-13', facilityId: 'facility-3', moduleId: 'mod-12' },
  // facility-4
  { id: 'fm-14', facilityId: 'facility-4', moduleId: 'mod-3' },
  { id: 'fm-15', facilityId: 'facility-4', moduleId: 'mod-4' },
  { id: 'fm-16', facilityId: 'facility-4', moduleId: 'mod-7' },
  { id: 'fm-17', facilityId: 'facility-4', moduleId: 'mod-10' },
  // facility-5
  { id: 'fm-18', facilityId: 'facility-5', moduleId: 'mod-1' },
  { id: 'fm-19', facilityId: 'facility-5', moduleId: 'mod-5' },
  { id: 'fm-20', facilityId: 'facility-5', moduleId: 'mod-8' },
  { id: 'fm-21', facilityId: 'facility-5', moduleId: 'mod-11' },
  { id: 'fm-22', facilityId: 'facility-5', moduleId: 'mod-14' },
  // Compliance Module — one IModularCompliance attachment per facility (ERC-3643 / SEP-0057)
  { id: 'fm-23', facilityId: 'facility-1', moduleId: 'mod-15' }, // Standard Institutional
  { id: 'fm-24', facilityId: 'facility-2', moduleId: 'mod-16' }, // Aggressive RWA
  { id: 'fm-25', facilityId: 'facility-3', moduleId: 'mod-15' }, // Standard Institutional
  { id: 'fm-26', facilityId: 'facility-4', moduleId: 'mod-17' }, // EU Qualified
  { id: 'fm-27', facilityId: 'facility-5', moduleId: 'mod-18' }, // Conservative Treasury
];
